import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { getChannelMeta } from "../../../lib/checkoutVerification";
import StorefrontModal from "./StorefrontModal";
import { logCustomerAuditEvent } from "../../../lib/customerAuditLog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const RETAIL_BASE = `${BACKEND_URL}/api/retail/public`;

/**
 * VerificationModal — channel-agnostic OTP step. Sprint A wires only
 * the **email** channel; SMS / multi-channel flows drop in by extending
 * `requiredChannels` upstream and adding a new `channel === "sms"` case
 * to the issue/verify URLs (or sharing them — they already are).
 *
 * Lifecycle (per channel):
 *   idle  → issuing  → awaiting_code  → verifying  → verified
 *   any step → failed (with retry)
 *
 * Props:
 *   slug, catalogId, sessionId       — tenant + cart context
 *   customer                         — { name, email, phone, ... }
 *   requiredChannels                 — string[]   ("email" today; "sms" later)
 *   orderType                        — "pickup" | "delivery" | "takeout"
 *   onClose()                        — user dismiss
 *   onVerified(verifiedTokens)       — { email: { token, verifiedAt } }
 *   purpose                          — forwarded verbatim as `/otp/issue`'s
 *                                       `purpose` field. Defaults to
 *                                       "checkout" (the only value every
 *                                       existing call site has ever used —
 *                                       this default preserves that
 *                                       behavior exactly). The returning-
 *                                       customer past-orders flow passes
 *                                       "order_lookup" instead, which
 *                                       `routes/retail_otp.py` forces to a
 *                                       REQUIRED verdict regardless of the
 *                                       tenant's checkout OTP policy.
 *   phoneAccessAllowed               — Oct 2026. Only past orders and
 *                                       loyalty pass this, and only when
 *                                       the catalog payload says the tenant
 *                                       turned "Require OTP verification"
 *                                       off. For the SMS channel the modal
 *                                       then asks the server for a
 *                                       phone-access grant instead of
 *                                       sending a code: no code is sent and
 *                                       no code-entry step is shown. The
 *                                       server re-reads the tenant setting
 *                                       and answers 403 `otp_required` if
 *                                       OTP is in fact required, in which
 *                                       case this falls back to the normal
 *                                       OTP flow — the prop can never skip
 *                                       verification on its own.
 */
export default function VerificationModal({
  slug, catalogId, sessionId, customer, requiredChannels,
  orderType, onClose, onVerified, purpose = "checkout",
  phoneAccessAllowed = false,
}) {
  // We process channels sequentially. First not-verified gets the stage.
  const [activeIdx, setActiveIdx] = useState(0);
  const [tokens, setTokens] = useState({});   // { [channel]: { token, verifiedAt } }
  const [stage, setStage] = useState("idle"); // idle | issuing | awaiting | verifying | verified | failed
  const [errorMsg, setErrorMsg] = useState("");
  const [code, setCode] = useState("");
  const [issueResp, setIssueResp] = useState(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  // New-customer SMS consent fix (Aug 2026) — when the backend rejects
  // an SMS issue for missing consent evidence, this drives an inline
  // "check the box, then send" recovery right here instead of leaving
  // the customer stuck on a "Try again" button that would just fail
  // the exact same way again. Separate from the caller-supplied
  // `customer.smsConsent` (e.g. `WelcomeModal`'s own checkbox) — this
  // modal has no way to mutate that prop, so it tracks its own local
  // consent state for this one retry.
  const [needsSmsConsent, setNeedsSmsConsent] = useState(false);
  // Gate C5A-3 — seconds remaining on a server-declared send block, from
  // the response's own `retry_after_seconds`. Never invented client-side:
  // when the server does not say, this stays 0 and the button behaves as
  // it always did.
  const [sendBlockedUntil, setSendBlockedUntil] = useState(0);
  const [localSmsConsent, setLocalSmsConsent] = useState(false);
  const codeInputRef = useRef(null);
  // Guard against React 18 StrictMode's intentional double-invoke of
  // mount effects in dev, AND against ever auto-issuing more than once
  // per channel. Tracks the `activeIdx` a code was already issued for
  // (rather than a plain boolean reset by a second, separately-ordered
  // effect) so there is no cross-effect ordering hazard: a boolean
  // reset via its own `useEffect` runs AFTER this file's auto-issue
  // effect within the same commit (effects run in declaration order),
  // which starved a legitimate multi-channel advance (e.g. email verified
  // -> sms next) of ever seeing the reset before the auto-issue effect's
  // own guard check already ran and bailed out for that render.
  const issuedForIdxRef = useRef(-1);
  // Refs keep the latest closures of `issueCode` + `meta` reachable to
  // the auto-issue effect below without carrying them (or their state
  // deps) into the effect's dependency array.
  const issueCodeRef = useRef(null);
  const metaLabelRef = useRef(null);
  // Set once the server refuses a phone-access grant with
  // `otp_required` — every later attempt in this modal uses OTP.
  const phoneAccessRefusedRef = useRef(false);

  const activeChannel = requiredChannels[activeIdx];
  const meta = getChannelMeta(activeChannel);
  metaLabelRef.current = meta?.label;
  // `issueCode` is a hoisted function declaration further down this
  // component, so it's safe to assign here even though its source
  // text appears later — this was previously missing entirely, which
  // left `issueCodeRef.current` permanently null and made the
  // mount-triggered auto-issue effect below a silent no-op for every
  // channel (see `void issueCodeRef.current?.()`).
  issueCodeRef.current = issueCode;
  const destinationValue = useMemo(() => {
    if (!meta) return "";
    return (customer?.[meta.readField] || "").trim();
  }, [customer, meta]);

  // Kick off the first issue automatically when the modal opens.
  useEffect(() => {
    if (!activeChannel) return;
    if (stage === "verified" || stage === "issuing" || stage === "awaiting" || stage === "looking_up") return;
    if (!destinationValue) {
      setStage("failed");
      setErrorMsg(`Please provide your ${metaLabelRef.current?.toLowerCase() || "contact"} first.`);
      return;
    }
    if (issuedForIdxRef.current === activeIdx) return;
    // Gate C5A-3 — never auto-issue into a server-declared block. The
    // per-mount guard above already stops a rerender from resending, but
    // it resets when this modal UNMOUNTS and remounts (close and reopen,
    // or a parent that recreates it), and each of those remounts used to
    // spend another send from the destination's hourly allowance without
    // the customer pressing anything.
    if (sendBlockedUntil > 0) return;
    issuedForIdxRef.current = activeIdx;
    void issueCodeRef.current?.();
  }, [activeChannel, stage, destinationValue, activeIdx, sendBlockedUntil]);

  // Gate C5A-3 — count the send block down so the customer can see when
  // they may try again, instead of a button that fails on every press.
  useEffect(() => {
    if (sendBlockedUntil <= 0) return;
    const t = setInterval(() => setSendBlockedUntil((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [sendBlockedUntil]);

  // Cooldown countdown for resend button (60s).
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setInterval(() => setResendCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendCooldown]);

  // PILOT-1B (Feb 2026) — customer-friendly, spam-aware copy.
  // We never imply successful delivery — only that the request was
  // accepted by SendGrid. Wording deliberately mirrors the standard
  // text used by Google / Apple / Stripe for OTP flows.
  const SEND_COPY = "Verification email sent. Check your Inbox — and your Spam/Junk folder if you don't see it within a minute. You can request another code after the timer expires.";

  // Gate C5A-3 — every send-side rejection the backend can emit, in
  // words a customer can act on. Anything unrecognised falls through to
  // a generic line rather than exposing the code: a string a customer
  // cannot act on is not worth the leak, and support has the console
  // entry and the server log either way.
  function formatWait(seconds) {
    const s = Math.max(0, Math.ceil(Number(seconds) || 0));
    if (s <= 0) return null;
    if (s < 60) return `${s} second${s === 1 ? "" : "s"}`;
    const mins = Math.ceil(s / 60);
    if (mins < 60) return `${mins} minute${mins === 1 ? "" : "s"}`;
    const hours = Math.ceil(mins / 60);
    return `${hours} hour${hours === 1 ? "" : "s"}`;
  }

  function friendlySendError(detail) {
    const code = (detail && (detail.error || detail.code)) || "";
    const wait = formatWait(detail && detail.retry_after_seconds);
    switch (code) {
      case "otp_destination_hourly_limit":
      case "otp_resend_limit":
      case "otp_daily_cap":
        return wait
          ? `Too many verification requests. Please wait about ${wait} before requesting another code.`
          : "Too many verification requests. Please wait before requesting another code.";
      case "otp_cooldown_active":
        return wait
          ? `Please wait ${wait} before requesting another verification code.`
          : "Please wait a moment before requesting another verification code.";
      case "otp_provider_not_configured":
        return "We can't send a verification code right now. Please contact the restaurant to place your order.";
      case "sms_consent_required":
        return "Please agree to receive verification codes by SMS, then send the code.";
      default:
        return "We couldn't send the verification code right now. Please try again in a moment.";
    }
  }

  // Mirrors the server's own `RETAIL_OTP_ISSUE_COOLDOWN` default. Only a
  // display/eligibility hint — the server re-checks it on every issue and
  // remains the authority.
  const ISSUE_COOLDOWN_SECONDS = 30;

  // A failure that may have sent a code anyway: no response at all
  // (network drop, timeout, aborted request), or a 5xx raised after the
  // server started work. A 4xx is a definite refusal — nothing was sent —
  // so it stays immediately retryable.
  function isAmbiguousSendFailure(err) {
    const status = err?.response?.status;
    if (status === undefined || status === null) return true;
    return Number(status) >= 500;
  }

  // Codes where retrying immediately is guaranteed to fail again. The
  // old UI showed a prominent "Try again" for these, which called the
  // very endpoint that had just rate-limited the customer — adding a
  // request to the window that was already over its cap.
  const SEND_BLOCKED_CODES = new Set([
    "otp_destination_hourly_limit",
    "otp_resend_limit",
    "otp_daily_cap",
    "otp_cooldown_active",
  ]);

  // Map backend `error` codes to customer-facing messages.
  // We deliberately do NOT expose internal codes (svc.OtpVerifyError
  // raises e.g. otp_code_mismatch, otp_expired, otp_locked, otp_not_found).
  function friendlyOtpError(detail) {
    const code = (detail && (detail.error || detail.code)) || "";
    const remaining =
      (detail && typeof detail.attempts_remaining === "number")
        ? detail.attempts_remaining : null;
    if (code === "otp_expired") {
      return { kind: "expired",
        text: "This verification code has expired. Please request a new code below." };
    }
    if (code === "otp_locked") {
      return { kind: "locked",
        text: "Too many attempts. Please request a new verification code." };
    }
    if (code === "otp_invalidated") {
      return { kind: "expired",
        text: "This code is no longer valid because a newer code was sent. Please use the latest code from your inbox." };
    }
    if (code === "otp_already_verified") {
      return { kind: "info", text: "This email has already been verified." };
    }
    if (code === "otp_code_mismatch" || code === "otp_not_found") {
      const suffix = (remaining !== null)
        ? ` You have ${remaining} attempt${remaining === 1 ? "" : "s"} remaining.`
        : "";
      return { kind: "wrong",
        text: `The verification code entered is incorrect. Please try again.${suffix}` };
    }
    return { kind: "unknown",
      text: "We couldn't verify that code. Please try again or request a new one." };
  }

  // Focus the code input when we land on awaiting.
  useEffect(() => {
    if (stage === "awaiting") {
      setTimeout(() => codeInputRef.current?.focus(), 50);
    }
  }, [stage]);

  // Single-session OTP dedup fix (Aug 2026) — shared by the auto-verified
  // early-return path below AND `verifyCode`'s normal success path, so
  // "this channel is done" is decided in exactly one place. Advances to
  // the next required channel (re-entering `idle` so its own auto-issue
  // effect fires), or — once every channel is covered — hands the full
  // tokens map back upstream via `onVerified`.
  function advanceAfterChannelVerified(nextTokens) {
    setTokens(nextTokens);
    if (activeIdx + 1 < requiredChannels.length) {
      setActiveIdx(activeIdx + 1);
      setStage("idle");
      setCode("");
      setIssueResp(null);
    } else {
      setStage("verified");
      onVerified(nextTokens);
    }
  }

  // Oct 2026 — the no-OTP path. Returns true when it completed the
  // channel (or showed an error), false when the caller should run the
  // normal OTP issuance instead.
  async function tryPhoneAccess() {
    if (!phoneAccessAllowed || activeChannel !== "sms" || phoneAccessRefusedRef.current) {
      return false;
    }
    setStage("looking_up");
    setErrorMsg("");
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/public/retail/${slug}/phone-access`,
        { catalog_id: catalogId, phone: destinationValue },
      );
      advanceAfterChannelVerified({
        ...tokens,
        sms: {
          token: data.checkout_attempt_token,
          verifiedAt: data.verified_at || new Date().toISOString(),
          phoneAccess: true,
        },
      });
      return true;
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 403 && detail?.code === "otp_required") {
        // The tenant requires OTP after all — the server decides.
        phoneAccessRefusedRef.current = true;
        return false;
      }
      setStage("failed");
      setErrorMsg(
        (detail && typeof detail === "object" && detail.message)
          || "We couldn't look up that number right now. Please try again in a moment.",
      );
      return true;
    }
  }

  async function issueCode(opts = {}) {
    if (await tryPhoneAccess()) return;
    setStage("issuing");
    setErrorMsg("");
    setCode("");
    setNeedsSmsConsent(false);
    try {
      // P0.2 (Feb 2026 SMS Sprint) — when the primary channel is email
      // AND the customer supplied an SMS-consented phone, ask the
      // backend to dispatch the SAME 6-digit code by SMS as well. The
      // verification path stays backward-compatible: either the email
      // or SMS code (identical value) verifies against the same
      // checkout_attempt_token.
      // New-customer SMS consent fix (Aug 2026) — `opts.forceSmsConsent`
      // is set only by this modal's own inline "check the box, then
      // send" recovery button below, right after the customer just
      // checked it in THIS modal — never defaulted or inferred.
      const consentChecked = !!customer?.smsConsent || !!opts.forceSmsConsent;
      const hasPhone = !!(customer?.phone || "").trim();
      const alsoSendSmsTo = (activeChannel === "email" && consentChecked && hasPhone)
        ? (customer.phone || "").trim()
        : null;
      // Explicit diagnostic so we never guess "did we send also_send_sms_to"
      // from a distance again — operators can grab this from DevTools.
      // Redacted: only last-4 of phone.
      const phoneTail = alsoSendSmsTo ? String(alsoSendSmsTo).slice(-4) : "n/a";
      console.info(
        "[otp.issue] plan",
        { activeChannel, consentChecked, hasPhone, willSendSms: !!alsoSendSmsTo, phoneTail },
      );
      const { data } = await axios.post(`${RETAIL_BASE}/${slug}/otp/issue`, {
        session_id: sessionId,
        catalog_id: catalogId,
        destination_channel: activeChannel,
        destination_value: destinationValue,
        fulfillment_mode: orderType,
        purpose,
        ...(alsoSendSmsTo ? { also_send_sms_to: alsoSendSmsTo } : {}),
        // New-customer SMS consent fix (Aug 2026) — forwards the SAME
        // consent-checkbox state the caller already gates its own
        // auto-trigger on (`WelcomeModal`) or already collected earlier
        // in checkout (`CheckoutModal`'s `customer.smsConsent`). The
        // backend is the actual authority — it independently rejects an
        // SMS issuance with no consent evidence on either this flag or
        // an existing customer record — this is just the honest signal
        // of what the caller observed, never trusted as sufficient by
        // itself.
        ...(activeChannel === "sms" ? {
          sms_consent_given: consentChecked,
          // Durable SMS-consent audit correction (Aug 2026, PR #121
          // follow-up) — forwards the SAME disclosure-version/source-
          // URL fields `CheckoutModal`'s checkout-time consent
          // envelope already sends, so the backend's durable consent
          // EVENT (created only when `consentChecked` is true) records
          // exactly what the customer saw, not a generic default.
          // Falls back to this page's own URL when `customer` (e.g.
          // WelcomeModal's minimal `{ phone, email, smsConsent }`
          // prop) carries neither field.
          ...(consentChecked ? {
            sms_consent_disclosure_version: customer?.smsConsentTextVersion || "v1.0",
            sms_consent_source_url: (customer?.smsConsentSourceUrl
              || (typeof window !== "undefined" ? window.location.href : "")).slice(0, 512),
          } : {}),
        } : {}),
      });
      // Single-session OTP dedup fix (Aug 2026) — the backend already
      // found a still-valid VERIFIED grant for this exact destination
      // (e.g. the customer verified this same phone number earlier this
      // session at the Welcome step) and deliberately sent NO new code.
      // Skip the code-entry screen entirely and complete this channel
      // immediately — asking the customer to "enter the code we just
      // sent" when no code was sent would strand them.
      if (data?.already_verified && data?.checkout_attempt_token) {
        console.info("[otp.issue] response: reused existing verified grant, no new code sent", {
          checkout_attempt_token: data.checkout_attempt_token,
        });
        advanceAfterChannelVerified({
          ...tokens,
          [activeChannel]: {
            token: data.checkout_attempt_token,
            verifiedAt: data.verified_at || new Date().toISOString(),
            reused: true,
          },
        });
        return;
      }
      // Log the FULL response envelope (redacted server-side) so the
      // reason SMS did or didn't fire is discoverable in the browser.
      console.info(
        "[otp.issue] response",
        {
          checkout_attempt_token: data?.checkout_attempt_token,
          email_dispatch: data?.dispatch,
          sms_dispatch: data?.sms_dispatch || null,
        },
      );
      setIssueResp({
        ...data,
        // Stash the local decision so the UI can show WHY we didn't send SMS.
        _plan: {
          consentChecked, hasPhone,
          willSendSms: !!alsoSendSmsTo,
          phoneTail,
        },
      });
      setStage("awaiting");
      setResendCooldown(60);
      // PILOT-1B — never imply delivery. SendGrid 202 = accepted, not delivered.
      // Toast text intentionally tells the user to look in Spam if they don't
      // see the email within a minute (Required Changes 1 + 7).
      if (data?.dev_code) {
        toast.success(`Dev code: ${data.dev_code}`, { duration: 8000 });
      } else {
        toast.success(SEND_COPY, { duration: 7000 });
        // P0.2 — SMS toast only when it actually shipped. When it didn't,
        // we surface the reason inline on the modal (not a toast) so it
        // persists and is easy to grab in a support call.
        if (data?.sms_dispatch?.sent) {
          toast.success("A copy was also sent by SMS to your phone.", { duration: 6000 });
        }
      }
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      // Full-console debug so operators can grab the exact server
      // envelope from DevTools when the user tone tightens.
      console.error("[otp.issue] rejected", { status, detail });
      let msg = null;
      if (typeof detail === "string" && detail.trim()) {
        msg = detail;
      } else if (detail && typeof detail === "object") {
        // Recognise the two structured shapes the backend actually
        // emits for /otp/issue. `otp_cooldown_active` includes a
        // `retry_after_seconds` hint (P0.2 hard rate-limit) which we
        // surface so the customer isn't left guessing.
        if (detail.message) {
          msg = detail.message;
        } else if (detail.error === "otp_cooldown_active") {
          const secs = Number(detail.retry_after_seconds || 30);
          msg = `Please wait ${secs} second${secs === 1 ? "" : "s"} before requesting another verification code.`;
        } else if (detail.error) {
          // Gate C5A-3 (Sep 2026) — this branch used to render the raw
          // internal code, so a customer who hit the per-destination
          // hourly cap was shown, verbatim:
          //   "Verification code error — otp_destination_hourly_limit.
          //    Please try again in a moment."
          // Three things wrong with that. It leaks an internal
          // identifier; it means nothing to the person reading it; and
          // "in a moment" was false — the authoritative retry window for
          // that particular limit is an hour, which the same response
          // already carried in `retry_after_seconds` and which this code
          // threw away. The raw code is still logged to the console
          // above for support, where it belongs.
          msg = friendlySendError(detail);
        }
      }
      if (!msg) {
        msg = "We couldn't send the verification code right now. Please try again in a moment.";
      }
      // New-customer SMS consent fix (Aug 2026) — drives the inline
      // "check the box, then send" recovery in the failed-stage render
      // below instead of leaving a plain "Try again" that would just
      // fail identically on every retry.
      if (detail && typeof detail === "object" && detail.error === "sms_consent_required") {
        setNeedsSmsConsent(true);
      }
      // Gate C5A-3 — remember that sending is blocked, and for how long,
      // so the failed stage below can withhold a button whose only
      // possible outcome is another rejected request.
      const blockedCode = detail && typeof detail === "object" ? detail.error : null;
      if (blockedCode && SEND_BLOCKED_CODES.has(blockedCode)) {
        setSendBlockedUntil(Math.max(1, Math.ceil(Number(detail.retry_after_seconds) || 0)));
      } else if (isAmbiguousSendFailure(e)) {
        // Gate C5A-3A — an ambiguous failure is one where the request may
        // ALREADY have reached the provider and sent a code: a network
        // error with no response at all, a timeout, or a 5xx after the
        // server had begun handling it. Re-issuing on top of that is how
        // one customer action becomes two SMS messages and two entries
        // against the destination's hourly allowance.
        //
        // Nothing is retried automatically here — nothing ever was. What
        // this adds is that the explicit retry is withheld for the
        // server's own issue cooldown, so the customer cannot press a
        // button whose most likely outcome is a second code for a send
        // that already worked. After the cooldown they may retry, which
        // is exactly the "returns to an eligible state" rule.
        setSendBlockedUntil(ISSUE_COOLDOWN_SECONDS);
      } else {
        setSendBlockedUntil(0);
      }
      setStage("failed");
      setErrorMsg(msg);
    }
  }

  async function resendCode() {
    if (resendCooldown > 0 || !issueResp?.checkout_attempt_token) return;
    setStage("issuing");
    setErrorMsg("");
    try {
      const { data } = await axios.post(`${RETAIL_BASE}/${slug}/otp/resend`, {
        session_id: sessionId,
        catalog_id: catalogId,
        destination_channel: activeChannel,
        destination_value: destinationValue,
        checkout_attempt_token: issueResp.checkout_attempt_token,
      });
      setIssueResp({ ...issueResp, ...data });
      setStage("awaiting");
      setResendCooldown(60);
      if (data?.dev_code) {
        toast.success(`Dev code: ${data.dev_code}`, { duration: 8000 });
      } else {
        toast.success(SEND_COPY, { duration: 7000 });
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg =
        detail?.message ||
        (typeof detail === "string" ? detail : null) ||
        "We couldn't resend the code right now. Please wait a moment and try again.";
      setStage("failed");
      setErrorMsg(msg);
    }
  }

  async function verifyCode() {
    const trimmed = (code || "").trim();
    if (!/^\d{4,8}$/.test(trimmed)) {
      setErrorMsg("Please enter the 6-digit code.");
      return;
    }
    setStage("verifying");
    setErrorMsg("");
    try {
      await axios.post(`${RETAIL_BASE}/${slug}/otp/verify`, {
        catalog_id: catalogId,
        checkout_attempt_token: issueResp.checkout_attempt_token,
        destination_channel: activeChannel,
        destination_value: destinationValue,
        code: trimmed,
      });
      advanceAfterChannelVerified({
        ...tokens,
        [activeChannel]: {
          token: issueResp.checkout_attempt_token,
          verifiedAt: new Date().toISOString(),
        },
      });
    } catch (e) {
      // PILOT-1B (Required Changes 2, 3, 9, 10): never show raw backend
      // codes. Map to friendly copy + remaining-attempts.
      const detail = e?.response?.data?.detail;
      const { kind, text } = friendlyOtpError(detail);
      if (kind === "expired" || kind === "locked") {
        // Keep modal open in "awaiting" with an explicit Resend CTA —
        // the existing Resend button below the input is already there.
        // Clear the input so the next code goes into an empty field.
        setCode("");
      }
      setStage("awaiting");
      setErrorMsg(text);
    }
  }

  if (!activeChannel) return null;

  const masked = (() => {
    if (!destinationValue) return "";
    if (activeChannel === "email") {
      const [u, d] = destinationValue.split("@");
      if (!d || !u) return destinationValue;
      const head = u.slice(0, 2);
      return `${head}${"•".repeat(Math.max(1, u.length - 2))}@${d}`;
    }
    return destinationValue.replace(/.(?=.{4})/g, "•");
  })();

  return (
    <StorefrontModal
      testId="retail-verification-modal"
      onCancel={onClose}
      onBackdropBlocked={() => logCustomerAuditEvent(slug, { event: "modal_close_blocked", orderType, reasonCode: "backdrop_click_blocked", catalogId: catalogId, sessionId, customerId: customer?.id })}
      title="Verify it's you"
      description={meta?.description || "Enter the code we sent you"}
      zIndexClassName="z-[60]"
    >
      <div className="text-stone-900">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-serif text-2xl">Verify it&apos;s you</h2>
          <span className="text-2xl" aria-hidden>{meta?.icon}</span>
        </div>
        <p className="text-sm text-stone-600">
          {meta?.description}
        </p>

        {/* Progress dots for multi-channel future flow */}
        {requiredChannels.length > 1 && (
          <div className="mt-3 flex items-center gap-2" data-testid="retail-verification-progress">
            {requiredChannels.map((ch, i) => (
              <span
                key={ch}
                className={
                  "h-2 w-8 rounded-full " +
                  (i < activeIdx
                    ? "bg-emerald-500"
                    : i === activeIdx
                    ? "bg-orange-500"
                    : "bg-stone-200")
                }
                aria-label={`Step ${i + 1} of ${requiredChannels.length}: ${ch}`}
              />
            ))}
          </div>
        )}

        <div className="mt-4 rounded-xl bg-stone-50 border border-stone-200 p-3 text-sm">
          <div className="text-stone-500">Verifying</div>
          <div className="font-medium" data-testid="retail-verification-target">{masked || "(missing)"}</div>
        </div>

        {/* P0.2 Diagnostic (Feb 2026) — always show whether SMS was
            attempted, and if it wasn't, WHY. Removes the ambiguity that
            burned a support cycle in the first prod checkout. Never
            renders the full phone — just the last 4 digits. */}
        <SmsDispatchPill
          activeChannel={activeChannel}
          plan={issueResp?._plan}
          smsDispatch={issueResp?.sms_dispatch}
        />

        {stage === "looking_up" && (
          <div className="mt-5 flex items-center justify-center gap-3 text-stone-600 text-sm" data-testid="retail-phone-access-loading">
            <span className="inline-block h-4 w-4 rounded-full border-2 border-orange-500 border-t-transparent animate-spin" />
            Looking up your account…
          </div>
        )}

        {(stage === "issuing" || stage === "verifying") && (
          <div className="mt-5 flex items-center justify-center gap-3 text-stone-600 text-sm" data-testid="retail-verification-loading">
            <span className="inline-block h-4 w-4 rounded-full border-2 border-orange-500 border-t-transparent animate-spin" />
            {stage === "issuing" ? "Sending code…" : "Verifying…"}
          </div>
        )}

        {stage === "awaiting" && (
          <>
            <label className="mt-4 block text-xs uppercase tracking-wider text-stone-500" htmlFor="otp-code-input">
              Enter the 6-digit code
            </label>
            <input
              id="otp-code-input"
              ref={codeInputRef}
              data-testid="retail-otp-code-input"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={8}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/[^\d]/g, ""))}
              onKeyDown={(e) => { if (e.key === "Enter") verifyCode(); }}
              placeholder="123456"
              className="mt-1 w-full h-12 rounded-lg border-2 border-stone-300 px-3 text-2xl tracking-[0.4em] text-center font-mono focus:border-orange-500 focus:outline-none"
            />
            {errorMsg && (
              <div className="mt-2 text-sm text-rose-600" data-testid="retail-verification-error">{errorMsg}</div>
            )}
            <button
              type="button"
              onClick={verifyCode}
              disabled={!code || code.length < 4}
              data-testid="retail-otp-verify-btn"
              className={
                "mt-4 w-full h-12 rounded-lg font-semibold text-white " +
                (!code || code.length < 4
                  ? "bg-orange-500/40 cursor-not-allowed"
                  : "bg-orange-500 hover:bg-orange-600 active:scale-[0.99]")
              }
            >
              Verify
            </button>
            <div className="mt-3 flex items-center justify-between text-sm">
              <button
                type="button"
                disabled={resendCooldown > 0}
                onClick={resendCode}
                data-testid="retail-otp-resend-btn"
                className={resendCooldown > 0 ? "text-stone-400" : "text-orange-600 hover:underline"}
              >
                {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend code"}
              </button>
              <button
                type="button"
                onClick={onClose}
                data-testid="retail-otp-cancel-btn"
                className="text-stone-500 hover:underline"
              >
                Cancel
              </button>
            </div>
          </>
        )}

        {stage === "failed" && (
          <>
            <div className="mt-4 text-sm text-rose-600" data-testid="retail-verification-error">
              {errorMsg || "Something went wrong."}
            </div>
            {/* New-customer SMS consent fix (Aug 2026) — the backend
                rejected this SMS issue for missing consent evidence.
                A plain "Try again" would fail identically every time
                since nothing about the request changed; offer the
                explicit consent + send action the customer actually
                needs instead. */}
            {needsSmsConsent && activeChannel === "sms" ? (
              <>
                <label
                  className="mt-4 flex items-start gap-2 text-xs text-stone-600 leading-snug cursor-pointer"
                  data-testid="retail-verification-sms-consent-label"
                >
                  <input
                    type="checkbox"
                    data-testid="retail-verification-sms-consent"
                    checked={localSmsConsent}
                    onChange={(e) => setLocalSmsConsent(e.target.checked)}
                    className="mt-0.5 h-4 w-4 accent-orange-500 cursor-pointer shrink-0"
                  />
                  <span>I agree to receive verification codes via SMS. Message and data rates may apply.</span>
                </label>
                <button
                  type="button"
                  onClick={() => issueCode({ forceSmsConsent: localSmsConsent })}
                  disabled={!localSmsConsent}
                  data-testid="retail-otp-send-with-consent-btn"
                  className={
                    "mt-3 w-full h-12 rounded-lg font-semibold text-white " +
                    (!localSmsConsent
                      ? "bg-orange-500/40 cursor-not-allowed"
                      : "bg-orange-500 hover:bg-orange-600")
                  }
                >
                  Send verification code
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => issueCode()}
                disabled={sendBlockedUntil > 0}
                data-testid="retail-otp-retry-btn"
                className={
                  "mt-4 w-full h-12 rounded-lg font-semibold text-white " +
                  (sendBlockedUntil > 0
                    ? "bg-orange-500/40 cursor-not-allowed"
                    : "bg-orange-500 hover:bg-orange-600")
                }
              >
                {sendBlockedUntil > 0
                  ? `Try again in ${formatWait(sendBlockedUntil)}`
                  : "Try again"}
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              data-testid="retail-otp-cancel-btn"
              className="mt-2 w-full h-11 rounded-lg border border-stone-300 text-sm text-stone-600 hover:bg-stone-50"
            >
              Cancel
            </button>
          </>
        )}

        {stage === "verified" && (
          <div className="mt-5 text-center text-emerald-700" data-testid="retail-verification-success">
            <div className="text-3xl">✅</div>
            <div className="font-semibold mt-1">Verified</div>
          </div>
        )}
      </div>
    </StorefrontModal>
  );
}

/**
 * P0.2 Diagnostic pill (Feb 2026 SMS Sprint).
 *
 * Rendered under the "Verifying" tile in the OTP modal to remove any
 * ambiguity about the SMS arm of the dual-channel dispatch. Reads the
 * local plan (why the frontend did or didn't ask for SMS) AND the
 * server's `sms_dispatch` outcome envelope, then surfaces a single
 * concrete state line. Never renders the full phone — last-4 only.
 *
 * States:
 *   - success:   "SMS: sent to *********1234"
 *   - configured but silent: "SMS: not configured for this tenant"
 *   - no consent:            "SMS: consent not given — check the box on the Sales Order screen"
 *   - no phone:              "SMS: no phone on file"
 *   - not applicable:        (hidden — non-email primary channel)
 */
function SmsDispatchPill({ activeChannel, plan, smsDispatch }) {
  if (activeChannel !== "email" || !plan) return null;
  let tone = "warn";     // "ok" | "warn" | "info"
  let label = "";
  if (plan.willSendSms) {
    if (smsDispatch?.sent) {
      tone = "ok";
      label = `SMS: sent to *********${plan.phoneTail}`;
    } else if (smsDispatch?.reason === "sms_provider_not_configured") {
      tone = "warn";
      label = "SMS: not configured for this tenant";
    } else if (smsDispatch?.reason === "companion_issue_failed") {
      tone = "warn";
      label = "SMS: companion row rejected — see backend logs";
    } else if (smsDispatch?.reason === "sms_exception") {
      tone = "warn";
      label = "SMS: transient error — try Resend";
    } else if (smsDispatch) {
      tone = "warn";
      label = `SMS: ${smsDispatch.reason || "unknown state"}`;
    } else {
      tone = "info";
      label = "SMS: requested — awaiting server confirmation";
    }
  } else if (!plan.consentChecked) {
    tone = "info";
    label = "SMS: consent not given — check the box on the Sales Order screen to also receive by SMS";
  } else if (!plan.hasPhone) {
    tone = "info";
    label = "SMS: no phone on file";
  } else {
    return null;
  }
  const styles = {
    ok:   "bg-emerald-50 border-emerald-200 text-emerald-800",
    warn: "bg-amber-50 border-amber-200 text-amber-800",
    info: "bg-stone-50 border-stone-200 text-stone-600",
  }[tone];
  return (
    <div
      className={`mt-2 text-xs px-3 py-2 rounded-lg border ${styles}`}
      data-testid="retail-sms-dispatch-pill"
    >
      {label}
    </div>
  );
}
